import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiUrlBuilderService } from '../api-url-builder.service';
import type {
  DrivePickerFileMetadata,
  DrivePickerTokenResponse,
} from './drive-picker.models';

@Injectable({
  providedIn: 'root',
})
export class DrivePickerService {
  private gapiScriptPromise: Promise<void> | null = null;
  private pickerApiPromise: Promise<void> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  private getPickerToken(): Observable<DrivePickerTokenResponse> {
    return this.http.post<DrivePickerTokenResponse>(
      this.urlBuilder.backendApi('drive-picker', 'token'),
      {}
    );
  }

  private loadGapiScript(): Promise<void> {
    if (this.gapiScriptPromise) {
      return this.gapiScriptPromise;
    }

    this.gapiScriptPromise = new Promise<void>((resolve, reject) => {
      if (typeof gapi !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google API script'));
      document.head.appendChild(script);
    }).catch((error) => {
      this.gapiScriptPromise = null;
      throw error;
    });

    return this.gapiScriptPromise;
  }

  private async loadPickerApi(): Promise<void> {
    if (this.pickerApiPromise) {
      return this.pickerApiPromise;
    }

    await this.loadGapiScript();

    this.pickerApiPromise = new Promise<void>((resolve, reject) => {
      gapi.load('picker', {
        callback: () => resolve(),
        onerror: (error: unknown) => {
          this.pickerApiPromise = null;
          reject(error ?? new Error('Failed to load Google Picker API'));
        },
      });
    });

    return this.pickerApiPromise;
  }

  openPicker(): Observable<DrivePickerFileMetadata | null> {
    return this.getPickerToken().pipe(
      switchMap(({ accessToken, appId }) =>
        from(this.showPicker(accessToken, appId))
      )
    );
  }

  private async showPicker(
    accessToken: string,
    appId: string
  ): Promise<DrivePickerFileMetadata | null> {
    await this.loadPickerApi();

    return new Promise<DrivePickerFileMetadata | null>((resolve, reject) => {
      const spreadsheetsView = new google.picker.DocsView(
        google.picker.ViewId.SPREADSHEETS
      );

      const csvView = new google.picker.DocsView();
      csvView.setMimeTypes('text/csv');

      const picker = new google.picker.PickerBuilder()
        .addView(spreadsheetsView)
        .addView(csvView)
        .setOAuthToken(accessToken)
        .setAppId(appId)
        .setTitle('Select a file to import')
        .setCallback((data: google.picker.ResponseObject) => {
          if (data.action === google.picker.Action.PICKED) {
            const doc = data.docs?.[0];
            if (!doc) {
              reject(new Error('File was selected but no document metadata was returned'));
              return;
            }
            resolve({
              fileId: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              url: doc.url,
            });
          } else if (data.action === google.picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();

      picker.setVisible(true);
    });
  }
}
